import type { ManualContentBlock, ManualSection } from './user-manual-types';
import { getFigureImage } from './user-manual-figures';

const t = (text: string): ManualContentBlock => ({ type: 'text', text });
const tl = (text: string, indent: 1 | 2 | 3): ManualContentBlock => ({
  type: 'text',
  text,
  indent,
});
const fi = (n: number, caption: string): ManualContentBlock => ({
  type: 'figure',
  caption,
  image: getFigureImage(n),
});

/** Section 5 — Understanding User Roles (manual text & figures 82–91). */
export const USER_MANUAL_SECTION_5: ManualSection[] = [
  {
    id: 'user-roles',
    title: '5. Understanding User Roles',
    level: 1,
    content: [
      'NHA supports multiple user roles, each designed to offer specific capabilities based on a user\'s purpose and responsibilities on the platform. Roles are upgradable through organisation affiliation.',
    ],
    figures: [],
  },
  {
    id: 'consumer-role',
    title: '5.1. Consumer / Consumer with Compute',
    level: 2,
    content: [],
    figures: [],
  },
  {
    id: 'consumer-access',
    title: '5.1.1. Access and Permissions',
    level: 2,
    content: [],
    contentBlocks: [
      tl(
        '1. Consumers can browse and search Datasets, Models, and Use cases.',
        1,
      ),
      tl(
        '2. They can view asset details, download open assets and request access to restricted assets.',
        1,
      ),
      tl(
        '3. Consumers can also create and run notebooks for doing exploratory analysis of data and building basic models.',
        1,
      ),
      tl(
        '4. Consumers with compute have a right to request for GPU-hr credits to do advanced model training.',
        1,
      ),
    ],
    figures: [],
  },
  {
    id: 'consumer-screens',
    title: '5.1.2. Consumer-specific screens',
    level: 2,
    content: [],
    contentBlocks: [
      tl('1. View your personal dashboard', 1),
      fi(82, 'Figure 82 — Consumer Dashboard'),
      tl('2. View/download activity history', 1),
      fi(83, 'Figure 83 — My Activities'),
      tl('3. View/Resume past projects', 1),
      fi(84, 'Figure 84 — My Projects Section'),
    ],
    figures: [],
  },
  {
    id: 'provider-role',
    title: '5.2. Provider / Provider with Compute',
    level: 2,
    content: [],
    figures: [],
  },
  {
    id: 'provider-access',
    title: '5.2.1. Additional Access and Permissions',
    level: 2,
    content: [],
    contentBlocks: [
      tl(
        '1. Providers can contribute assets to the platform, including Datasets, Models, and Use cases.',
        1,
      ),
      tl(
        '2. Providers with Compute have additional access to training and testing models using GPU resources in the Sandbox IDE.',
        1,
      ),
    ],
    figures: [],
  },
  {
    id: 'provider-features',
    title: '5.2.2. Additional Provider-specific features',
    level: 2,
    content: [],
    contentBlocks: [
      tl(
        '1. Option to upload new assets (Datasets/Models/Use cases) on platform.',
        1,
      ),
      tl('2. Manage your uploaded assets.', 1),
      fi(85, 'Figure 85 — Manage Assets Section'),
      tl('3. Approve requests from users to download your assets.', 1),
      fi(86, 'Figure 86 — Asset Requests section'),
    ],
    figures: [],
  },
  {
    id: 'org-manager',
    title: '5.3. Organization Manager',
    level: 2,
    content: [],
    figures: [],
  },
  {
    id: 'org-manager-functions',
    title: '5.3.1. Core Functions',
    level: 2,
    content: [],
    contentBlocks: [
      t(
        'Organisation Managers hold administrative control for their organisation\'s presence on NHA. They are responsible for:',
      ),
      tl(
        '1. Managing the organisation\'s user base (add/remove users, assign roles)',
        1,
      ),
      fi(87, 'Figure 87 — Manage Users Section'),
      fi(88, 'Figure 88 — User Requests Section'),
      tl(
        '2. Reviewing and approving asset access requests (asset download requests/asset publication requests/role upgradation requests)',
        1,
      ),
      fi(89, 'Figure 89 — Manage Assets Section'),
      fi(90, 'Figure 90 — Asset Requests Section'),
      tl(
        '3. Maintaining the organisation profile and ensuring compliance with NHA policies',
        1,
      ),
    ],
    figures: [],
  },
  {
    id: 'role-upgradation',
    title: '5.4. Role Upgradation Process',
    level: 2,
    content: [],
    contentBlocks: [
      t('Step 1: Users start as Consumers by default. To upgrade their role:'),
      tl(
        '1. Users need to request a role upgrade to become eligible for accessing GPU-based compute',
        1,
      ),
      tl(
        '2. Users need to join an existing organisation or request to register a new organisation (if not listed). This is a mandatory requirement to become a provider.',
        1,
      ),
      t(
        'Step 2: After completing the above two processes, the user should request for role upgrade from their profile section',
      ),
      t(
        'Step 3: Upon approval, they may be assigned the role of Consumer with Compute, Provider, Provider with Compute, based on the approval provided:',
      ),
      tl(
        '1. The Organization manager approves the user requests to become a provider',
        1,
      ),
      tl(
        '2. The NHA administrators approve the user requests for awarding compute.',
        1,
      ),
      fi(91, 'Figure 91 — Role Upgradation Screen'),
    ],
    figures: [],
  },
];
